export type PendingApplication = {
  id: string;
  status: string;
  company_name: string;
  secure_link_generated_at?: string | null;
  secure_link_expires_at?: string | null;
  secureLinkConfigured?: boolean;
  signwell_document_id?: string | null;
  signed_at?: string | null;
  signed_pdf_key?: string | null;
};

async function workflowApi<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  const data = (response.headers.get('content-type') || '').includes('json')
    ? await response.json()
    : await response.text();
  if (!response.ok) throw new Error((data as any)?.error || `Erreur ${response.status}`);
  return data as T;
}

function e(value: unknown): string {
  return String(value ?? '').replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;',
  }[char] || char));
}

function localNowValue(): string {
  const date = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000);
  return date.toISOString().slice(0, 16);
}

function notify(message: string, error = false): void {
  let host = document.getElementById('workflowNoticeHost');
  if (!host) {
    host = document.createElement('div');
    host.id = 'workflowNoticeHost';
    host.className = 'live-toast-host';
    document.body.append(host);
  }
  const item = document.createElement('div');
  item.className = `live-toast ${error ? 'live-toast-error' : 'live-toast-success'} show`;
  item.textContent = message;
  host.append(item);
  window.setTimeout(() => item.remove(), error ? 5000 : 2800);
}

function enhancePendingAcceptance(detail: HTMLElement, id: string, app: PendingApplication): void {
  if (app.status !== 'APPROVED_WAITING_SIGNATURE') return;
  const decision = detail.querySelector<HTMLElement>('.decision-card');
  if (!decision) return;

  const expires = app.secure_link_expires_at ? new Date(app.secure_link_expires_at) : null;
  const expired = !expires || expires.getTime() <= Date.now();
  const generated = app.secure_link_generated_at ? new Date(app.secure_link_generated_at) : null;

  const panel = document.createElement('div');
  panel.className = 'pending-signature-panel';
  panel.innerHTML = `
    <div class="pending-signature-head">
      <strong>Acceptation enregistrée</strong>
      <span>La candidature n'est plus « À étudier ».</span>
    </div>
    <p>La demande de signature n'a pas encore été créée par SignWell. L'acceptation et le lien sécurisé restent enregistrés côté Joe.</p>
    ${generated ? `<p class="pending-meta">Lien généré : <strong>${e(generated.toLocaleString('fr-FR'))}</strong><br>Expiration : <strong>${e(expires?.toLocaleString('fr-FR') || 'inconnue')}</strong></p>` : ''}
    ${expired ? `
      <div class="warning compact-warning">Le lien sécurisé enregistré a expiré. Générez un nouveau lien dans le dashboard Joe avant de relancer la convention.</div>
      <label class="field"><span>Nouveau lien sécurisé</span><input id="retrySecureLink" type="url" placeholder="https://..."></label>
      <label class="field"><span>Date/heure de génération</span><input id="retryGeneratedAt" type="datetime-local" value="${localNowValue()}"></label>
    ` : ''}
    <button type="button" id="retrySignatureBtn" class="primary full">${expired ? 'Enregistrer le nouveau lien et envoyer la convention' : 'Réessayer l’envoi de la convention'}</button>
    <div id="retrySignatureMsg" class="errors"></div>
  `;
  decision.prepend(panel);

  panel.querySelector<HTMLButtonElement>('#retrySignatureBtn')?.addEventListener('click', async () => {
    const button = panel.querySelector<HTMLButtonElement>('#retrySignatureBtn')!;
    const message = panel.querySelector<HTMLElement>('#retrySignatureMsg')!;
    button.disabled = true;
    const old = button.textContent || '';
    button.textContent = 'Envoi en cours…';
    message.innerHTML = '';
    try {
      const body: Record<string, string> = {};
      if (expired) {
        const secureLink = (panel.querySelector<HTMLInputElement>('#retrySecureLink')?.value || '').trim();
        const local = panel.querySelector<HTMLInputElement>('#retryGeneratedAt')?.value || '';
        if (!secureLink || !local) throw new Error('Renseignez le nouveau lien sécurisé et sa date de génération.');
        body.secureLink = secureLink;
        body.generatedAt = new Date(local).toISOString();
      }
      const result = await workflowApi<{ invitationSent?: boolean; warning?: string }>(
        `/admin/api/admin/applications/${encodeURIComponent(id)}/retry-signature`,
        { method: 'POST', body: JSON.stringify(body) },
      );
      if (result.invitationSent) {
        notify('Convention envoyée au Partenaire pour signature.');
        window.setTimeout(() => location.reload(), 500);
      } else {
        message.innerHTML = `<div>${e(result.warning || 'L’acceptation est conservée mais l’invitation n’a pas pu être envoyée.')}</div>`;
        notify('Acceptation conservée. SignWell reste à relancer.', true);
      }
    } catch (error) {
      message.innerHTML = `<div>${e(error instanceof Error ? error.message : 'Envoi impossible.')}</div>`;
    } finally {
      button.disabled = false;
      button.textContent = old;
    }
  });
}

function enhanceExpiredLinkRecovery(detail: HTMLElement, id: string, app: PendingApplication): void {
  if (app.status !== 'EXPIRED_AFTER_SIGNATURE') return;
  const decision = detail.querySelector<HTMLElement>('.decision-card');
  if (!decision) return;

  const generated = app.secure_link_generated_at ? new Date(app.secure_link_generated_at) : null;
  const expires = app.secure_link_expires_at ? new Date(app.secure_link_expires_at) : null;
  const reusableStoredLink = Boolean(
    app.secureLinkConfigured &&
    generated &&
    expires &&
    Number.isFinite(expires.getTime()) &&
    expires.getTime() > Date.now(),
  );

  const panel = document.createElement('div');
  panel.className = 'pending-signature-panel';
  panel.innerHTML = `
    <div class="pending-signature-head">
      <strong>Convention signée — lien sécurisé expiré</strong>
      <span>La signature SignWell reste valide et archivée.</span>
    </div>
    <p>Ne renvoyez pas la convention : aucune nouvelle signature n'est nécessaire. Générez uniquement un nouveau lien sécurisé Joe afin de le transmettre au Partenaire.</p>
    ${app.signed_pdf_key ? '<div class="legal-note"><strong>PDF signé archivé.</strong> Le nouveau lien sera envoyé uniquement après contrôle de cette archive côté serveur.</div>' : '<div class="warning compact-warning">Archive signée non détectée. L’envoi sera refusé tant que le PDF signé n’est pas disponible.</div>'}
    ${reusableStoredLink ? `
      <div class="warning compact-warning">Un nouveau lien a déjà été enregistré et reste valide jusqu’au <strong>${e(expires!.toLocaleString('fr-FR'))}</strong>. Vous pouvez réessayer son envoi sans le ressaisir.</div>
    ` : `
      <label class="field"><span>Nouveau lien sécurisé</span><input id="reissueSecureLink" type="url" placeholder="https://..."></label>
      <label class="field"><span>Date/heure de génération</span><input id="reissueGeneratedAt" type="datetime-local" value="${localNowValue()}"><small>Expiration automatique : +24 h.</small></label>
    `}
    <button type="button" id="reissueSecureLinkBtn" class="primary full">${reusableStoredLink ? 'Réessayer l’envoi du lien sécurisé' : 'Envoyer le nouveau lien sécurisé'}</button>
    <div id="reissueSecureLinkMsg" class="errors"></div>
  `;
  decision.prepend(panel);

  panel.querySelector<HTMLButtonElement>('#reissueSecureLinkBtn')?.addEventListener('click', async () => {
    const button = panel.querySelector<HTMLButtonElement>('#reissueSecureLinkBtn')!;
    const message = panel.querySelector<HTMLElement>('#reissueSecureLinkMsg')!;
    const old = button.textContent || '';
    if (!confirm('Envoyer le lien sécurisé au Partenaire sans recréer de demande de signature ?')) return;

    button.disabled = true;
    button.textContent = 'Envoi en cours…';
    message.innerHTML = '';
    try {
      const body: Record<string, string> = {};
      if (!reusableStoredLink) {
        const secureLink = (panel.querySelector<HTMLInputElement>('#reissueSecureLink')?.value || '').trim();
        const local = panel.querySelector<HTMLInputElement>('#reissueGeneratedAt')?.value || '';
        if (!secureLink || !local) throw new Error('Renseignez le nouveau lien sécurisé et sa date de génération.');
        body.secureLink = secureLink;
        body.generatedAt = new Date(local).toISOString();
      }
      await workflowApi(
        `/admin/api/admin/applications/${encodeURIComponent(id)}/reissue-secure-link`,
        { method: 'POST', body: JSON.stringify(body) },
      );
      notify('Nouveau lien sécurisé envoyé. Aucune nouvelle signature n’a été créée.');
      window.setTimeout(() => location.reload(), 500);
    } catch (error) {
      const text = error instanceof Error ? error.message : 'Envoi impossible.';
      message.innerHTML = `<div>${e(text)}</div>`;
      notify(text, true);
    } finally {
      button.disabled = false;
      button.textContent = old;
    }
  });
}

export function enhanceApplicationWorkflow(
  detail: HTMLElement,
  id: string,
  app: PendingApplication,
): void {
  if (detail.dataset.workflowEnhanced === '1') return;
  enhancePendingAcceptance(detail, id, app);
  enhanceExpiredLinkRecovery(detail, id, app);
  detail.dataset.workflowEnhanced = '1';
}
